import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Company } from '../../../core/company/company.model';
import { IAppState } from '../../index';
import * as CompanyActions from '../actions/company.action';
import { CompanyService } from '../../../core/company/company.service';
import * as UserActions from '../../user/actions/user.action';
import * as ModalActions from '../../modal/actions/modal.action';
import { ToastComponent } from '../../../ui/toast/toast.component';
import { ErrorHandlingService } from '../../../shared/services/error-handling/error-handling.service';



@Injectable()
export class CompanyEffects {
    private readonly actions$ = inject(Actions);
    private readonly companyService = inject(CompanyService);
    private readonly store = inject(Store<IAppState>);
    private readonly errorService = inject(ErrorHandlingService);

    fetchCurrentCompany$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.fetch),
        switchMap(({ companyName, customDomain }) => {
          let company$: Observable<Company>;
          if (companyName) {
            company$ = this.companyService.getCompanyByName(companyName);
          } else if (customDomain) {
            company$ = this.companyService.getCompanyByCustomDomain(customDomain);
          } else {
            company$ = this.companyService.getCurrentCompany();
          }

          return company$.pipe(
            map(company => CompanyActions.changed({
              current: company,
            })),
            catchError(err => of(CompanyActions.apiError({ error: err }))),
          );
        })
      ));

    patch$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.patch),
        withLatestFrom(this.store),
        filter(([, state]) => !!state.company.current),
        switchMap(([action, state]) => {
          const req = {
            id: state.company.current.id,
            ...action.payload
          };
          return this.companyService.patchCompany(req).pipe(
            map(company => CompanyActions.patchSuccess({ company })),
            catchError(error => of(CompanyActions.apiError({ error: { name: 'patch', error } })))
          );
        })
      ));

    patchSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.patchSuccess),
        map(({ company }) => CompanyActions.changed({
          current: company,
        }))
      ));

    apiError$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.apiError),
        withLatestFrom(this.store),
        map(([action, state]) => CompanyActions.changed({
          errors: [
            action.error,
            ...(state.user.errors || [])
          ],
        }))
      ));

    /**
     * Update company settings effect
     * Calls the updateCompanySettings service method with the current company ID
     */
    updateSettings$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.updateSettings),
        withLatestFrom(this.store),
        filter(([, state]) => !!state.company.current?.id),
        switchMap(([action, state]) => {
          const companyId = state.company.current!.id;
          return this.companyService.updateCompanySettings(companyId, action.settings).pipe(
            map(settings => CompanyActions.updateSettingsSuccess({ settings })),
            catchError(error => of(CompanyActions.updateSettingsFailure({ error })))
          );
        })
      ));

    /**
     * Update settings success effect
     * Merges the updated settings into the current company state and shows success toast
     */
    updateSettingsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.updateSettingsSuccess),
        withLatestFrom(this.store),
        filter(([, state]) => !!state.company.current),
        mergeMap(([action, state]) => [
          CompanyActions.changed({
            current: {
              ...state.company.current!,
              settings: action.settings
            }
          }),
          ModalActions.open({
            cmpType: ToastComponent,
            props: {
              view: 'success',
              title: 'COMMON.SAVED',
            },
          })
        ])
      ));

    /**
     * Update settings failure effect
     * Handles errors by showing error message and adding them to the state
     */
    updateSettingsFailure$ = createEffect(() => this.actions$.pipe(
        ofType(CompanyActions.updateSettingsFailure),
        tap(({ error }) => {
          this.errorService.handleHttpErrors({
            errorResponse: error
          });
        }),
        map(action => CompanyActions.apiError({ error: { name: 'updateSettings', error: action.error } }))
      ));
}
